import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "lucide-react";

import {
  type PopupInput,
  PopupInputSchema,
  isoToDatetimeLocal,
  toPopupApiBody,
} from "@/features/popups/domain/popup.schema";
import { ImageUpload } from "@/features/popups/presentation/components/ImageUpload";
import { InlineError } from "@/shared/components/data/ErrorState";
import { FormSheet } from "@/shared/components/data/FormSheet";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import {
  Frequency,
  Platforms,
  Stage,
  useCreatePopupMutation,
  useUpdatePopupMutation,
} from "@/store/apis/adminApi.enhanced";
import type { Popup } from "@/store/apis/adminApi.enhanced";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  popup?: Popup | null;
}

const FORM_ID = "popup-form";

const STAGE_LABELS: Record<Stage, string> = {
  [Stage.Trying]: "Trying",
  [Stage.Fertility]: "Fertilidad",
  [Stage.Loss]: "Pérdida",
  [Stage.Lost]: "Lost",
};

const FREQUENCY_LABELS: Record<Frequency, string> = {
  [Frequency.Once]: "Una vez",
  [Frequency.Daily]: "Diario",
  [Frequency.EverySession]: "Cada sesión",
};

const PLATFORM_LABELS: Record<Platforms, string> = {
  [Platforms.Ios]: "iOS",
  [Platforms.Android]: "Android",
};

export function PopupFormSheet({ open, onOpenChange, popup }: Props) {
  const isEdit = Boolean(popup);

  const [createPopup, createState] = useCreatePopupMutation();
  const [updatePopup, updateState] = useUpdatePopupMutation();
  const submitting = createState.isLoading || updateState.isLoading;
  const submitError = createState.error ?? updateState.error;

  const defaults = useMemo<PopupInput>(
    () => ({
      title: popup?.title ?? "",
      body: popup?.body ?? "",
      image_url: popup?.image_url ?? "",
      cta_text: popup?.cta_text ?? "",
      cta_url: popup?.cta_url ?? "",
      target_stages: popup?.target_stages ?? [],
      platforms: popup?.platforms ?? [Platforms.Ios, Platforms.Android],
      frequency: popup?.frequency ?? Frequency.Once,
      priority: popup?.priority ?? 0,
      start_at: isoToDatetimeLocal(popup?.start_at),
      end_at: isoToDatetimeLocal(popup?.end_at),
      active: popup?.active ?? true,
    }),
    [popup],
  );

  const form = useForm<PopupInput>({
    resolver: zodResolver(PopupInputSchema),
    defaultValues: defaults,
    mode: "onBlur",
  });

  useEffect(() => {
    if (open) form.reset(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, popup?.id]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const body = toPopupApiBody(values);
      if (isEdit && popup) {
        await updatePopup({ id: popup.id, body }).unwrap();
        toast.success("Pop-up actualizado");
      } else {
        await createPopup({ body }).unwrap();
        toast.success("Pop-up creado");
      }
      onOpenChange(false);
    } catch {
      // InlineError.
    }
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Editar pop-up" : "Nuevo pop-up"}
      description={
        isEdit
          ? "Los cambios se publican al guardar."
          : "Configura el contenido, segmentación y cronograma del pop-up."
      }
      formId={FORM_ID}
      submitLabel={isEdit ? "Guardar cambios" : "Crear pop-up"}
      submitting={submitting}
      dirty={form.formState.isDirty}
      size="lg"
    >
      <Form {...form}>
        <form id={FORM_ID} onSubmit={onSubmit} className="space-y-8" noValidate>
          {/* ───── Contenido ───── */}
          <FormSection title="Contenido" description="Qué ve la usuaria.">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Título
                  </FormLabel>
                  <FormControl>
                    <Input
                      maxLength={100}
                      placeholder="Ej. Hablemos de ovulación"
                      className="h-11 text-[14px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Texto
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      maxLength={250}
                      placeholder="Mensaje breve, directo, empático…"
                      className="resize-none text-[14px] leading-relaxed"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Imagen
                  </FormLabel>
                  <FormControl>
                    <ImageUpload value={field.value ?? ""} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          {/* ───── CTA ───── */}
          <FormSection
            title="Llamada a acción"
            description="Opcional. Si pones texto, pon también el enlace."
          >
            <div className="grid grid-cols-[1fr_1.4fr] gap-4">
              <FormField
                control={form.control}
                name="cta_text"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      Texto del botón
                    </FormLabel>
                    <FormControl>
                      <Input
                        maxLength={40}
                        placeholder="Ver más"
                        className="h-11 text-[13.5px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cta_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      Enlace
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="arya://… o https://…"
                        className="h-11 font-mono text-[12.5px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>

          {/* ───── Segmentación ───── */}
          <FormSection title="Segmentación" description="A quién le aparece el pop-up.">
            <FormField
              control={form.control}
              name="target_stages"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Etapas
                  </FormLabel>
                  <ChipGroup<Stage>
                    options={Object.values(Stage)}
                    value={field.value}
                    onChange={field.onChange}
                    labels={STAGE_LABELS}
                  />
                  <FormDescription className="text-[12px]">
                    Si no eliges ninguna, el pop-up se muestra a todas las etapas.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="platforms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Plataformas
                  </FormLabel>
                  <ChipGroup<Platforms>
                    options={Object.values(Platforms)}
                    value={field.value}
                    onChange={field.onChange}
                    labels={PLATFORM_LABELS}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          {/* ───── Cronograma y comportamiento ───── */}
          <FormSection title="Cronograma" description="Cuándo aparece y con qué frecuencia.">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      Inicio
                    </FormLabel>
                    <FormControl>
                      <Input type="datetime-local" className="h-11 text-[13px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      Fin
                    </FormLabel>
                    <FormControl>
                      <Input type="datetime-local" className="h-11 text-[13px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      Frecuencia
                    </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="h-11 text-[13.5px]">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(Frequency).map((f) => (
                          <SelectItem key={f} value={f}>
                            {FREQUENCY_LABELS[f]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      Prioridad (0–100)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        className="h-11 text-[13.5px]"
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(Number(e.target.value || 0))}
                      />
                    </FormControl>
                    <FormDescription className="text-[12px]">
                      Mayor número aparece primero cuando hay varios candidatos.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem>
                  <div
                    className={cn(
                      "flex items-center justify-between gap-4 rounded-xl border bg-background p-4",
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-medium text-foreground">Pop-up activo</p>
                      <p className="text-[12px] text-muted-foreground">
                        Si lo desactivas, no se muestra aunque el cronograma lo cubra.
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </div>
                </FormItem>
              )}
            />
          </FormSection>

          <InlineError
            error={submitError}
            fallback={isEdit ? "No pudimos guardar los cambios." : "No pudimos crear el pop-up."}
          />
        </form>
      </Form>
    </FormSheet>
  );
}

/** Sección colapsable visual con título + descripción + divisor. */
function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="border-b border-border/60 pb-2.5">
        <h3 className="font-display text-[1rem] font-medium tracking-display-tight text-foreground">
          {title}
        </h3>
        {description ? (
          <p className="mt-0.5 text-[12.5px] text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

/** Group de chips tipo toggle multi-select. */
function ChipGroup<T extends string>({
  options,
  value,
  onChange,
  labels,
}: {
  options: readonly T[];
  value: T[];
  onChange: (next: T[]) => void;
  labels: Record<T, string>;
}) {
  const toggle = (opt: T) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const selected = value.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            onClick={() => toggle(opt)}
            aria-pressed={selected}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-medium",
              "transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              selected
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-background text-foreground hover:bg-muted/60",
            )}
          >
            {selected ? <Check className="h-3 w-3" /> : null}
            {labels[opt]}
          </button>
        );
      })}
    </div>
  );
}
